/* eslint-disable react-refresh/only-export-components */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Index } from "flexsearch";
import type { Place, PlacesManifest } from "./placeSearchTypes";

const DEFAULT_RESULT_LIMIT = 15;
const STORAGE_KEY_MANIFEST = "placeSearch.manifest";
const STORAGE_KEY_PLACES = "placeSearch.places";
const STORAGE_KEY_INDEX = "placeSearch.index";

const IDB_NAME = "sailmate-placeSearch";
const IDB_STORE = "cache";
const IDB_VERSION = 1;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE, { keyPath: "id" });
      }
    };
  });
}

async function readFromStorage<T>(key: string): Promise<T | null> {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, "readonly");
      const store = tx.objectStore(IDB_STORE);
      const req = store.get(key);
      req.onerror = () => {
        db.close();
        reject(req.error);
      };
      req.onsuccess = () => {
        db.close();
        const row = req.result as { id: string; data: T } | undefined;
        resolve(row?.data ?? null);
      };
    });
  } catch {
    return null;
  }
}

async function writeToStorage(key: string, value: unknown): Promise<void> {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      const store = tx.objectStore(IDB_STORE);
      const req = store.put({ id: key, data: value });
      req.onerror = () => {
        db.close();
        reject(req.error);
      };
      req.onsuccess = () => {
        db.close();
        resolve();
      };
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === "QuotaExceededError") {
      console.warn("[placeSearch] IndexedDB quota exceeded, cache not saved");
    }
  }
}

async function clearPlaceSearchStorage(): Promise<void> {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      const store = tx.objectStore(IDB_STORE);
      for (const key of [
        STORAGE_KEY_MANIFEST,
        STORAGE_KEY_PLACES,
        STORAGE_KEY_INDEX,
      ]) {
        store.delete(key);
      }
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  } catch {
    // ignore
  }
}

function getPlacesBaseUrl(): string {
  const placesUrl = import.meta.env.VITE_PLACES_URL as string | undefined;
  if (placesUrl && String(placesUrl).trim()) {
    const base = String(placesUrl).trim();
    return base.endsWith("/") ? base.slice(0, -1) : base;
  }
  const cdnUrl = import.meta.env.VITE_CDN_URL as string | undefined;
  if (!cdnUrl || !String(cdnUrl).trim()) {
    throw new Error(
      "Place search requires VITE_PLACES_URL or VITE_CDN_URL to be set",
    );
  }
  const base = String(cdnUrl).trim().endsWith("/")
    ? String(cdnUrl).trim().slice(0, -1)
    : String(cdnUrl).trim();
  return `${base}/map-search`;
}

async function fetchJson<T>(url: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(`Place search: ${msg} (${url})`);
  }
  if (!res.ok) throw new Error(`Place search: ${url} → ${res.status}`);
  return (await res.json()) as T;
}

interface PlaceSearchContextValue {
  search: (query: string, limit?: number) => Place[];
  places: Place[] | null;
  ready: boolean;
  error: Error | null;
  retry: () => void;
}

const PlaceSearchContext = createContext<PlaceSearchContextValue | null>(null);

export function PlaceSearchProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [places, setPlaces] = useState<Place[] | null>(null);
  const [index, setIndex] = useState<Index | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [loadKey, setLoadKey] = useState(0);

  const load = useCallback(async () => {
    setError(null);
    setReady(false);
    try {
      const [cachedPlaces, cachedIndex] = await Promise.all([
        readFromStorage<Place[]>(STORAGE_KEY_PLACES),
        readFromStorage<Record<string, string>>(STORAGE_KEY_INDEX),
      ]);

      if (
        Array.isArray(cachedPlaces) &&
        cachedPlaces.length > 0 &&
        cachedIndex &&
        typeof cachedIndex === "object"
      ) {
        const searchIndex = new Index({ tokenize: "forward", resolution: 9 });
        for (const [key, data] of Object.entries(cachedIndex)) {
          searchIndex.import(key, data);
        }
        setPlaces(cachedPlaces);
        setIndex(searchIndex);
        setReady(true);
        return;
      }

      const base = getPlacesBaseUrl();
      const manifestUrl = `${base}/places.manifest.json`;
      const manifest = await fetchJson<PlacesManifest>(manifestUrl);

      const cachedManifest =
        await readFromStorage<PlacesManifest>(STORAGE_KEY_MANIFEST);
      const manifestMatch =
        cachedManifest?.data === manifest.data &&
        cachedManifest?.index === manifest.index;

      let placesData: Place[];
      let indexData: Record<string, string>;

      if (manifestMatch) {
        const [cachedPlacesFallback, cachedIndexFallback] = await Promise.all([
          readFromStorage<Place[]>(STORAGE_KEY_PLACES),
          readFromStorage<Record<string, string>>(STORAGE_KEY_INDEX),
        ]);
        if (
          Array.isArray(cachedPlacesFallback) &&
          cachedPlacesFallback.length > 0 &&
          cachedIndexFallback &&
          typeof cachedIndexFallback === "object"
        ) {
          placesData = cachedPlacesFallback;
          indexData = cachedIndexFallback;
        } else {
          const dataUrl = `${base}/${manifest.data}`;
          const indexUrl = `${base}/${manifest.index}`;
          [placesData, indexData] = await Promise.all([
            fetchJson<Place[]>(dataUrl),
            fetchJson<Record<string, string>>(indexUrl),
          ]);
          await Promise.all([
            writeToStorage(STORAGE_KEY_MANIFEST, manifest),
            writeToStorage(STORAGE_KEY_PLACES, placesData),
            writeToStorage(STORAGE_KEY_INDEX, indexData),
          ]);
        }
      } else {
        const dataUrl = `${base}/${manifest.data}`;
        const indexUrl = `${base}/${manifest.index}`;
        [placesData, indexData] = await Promise.all([
          fetchJson<Place[]>(dataUrl),
          fetchJson<Record<string, string>>(indexUrl),
        ]);
        await Promise.all([
          writeToStorage(STORAGE_KEY_MANIFEST, manifest),
          writeToStorage(STORAGE_KEY_PLACES, placesData),
          writeToStorage(STORAGE_KEY_INDEX, indexData),
        ]);
      }

      const searchIndex = new Index({ tokenize: "forward", resolution: 9 });
      for (const [key, data] of Object.entries(indexData)) {
        searchIndex.import(key, data);
      }

      setPlaces(placesData);
      setIndex(searchIndex);
      setReady(true);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setPlaces(null);
      setIndex(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, loadKey]);

  const retry = useCallback(() => {
    void clearPlaceSearchStorage().then(() => setLoadKey((k) => k + 1));
  }, []);

  const search = useCallback(
    (query: string, limit: number = DEFAULT_RESULT_LIMIT): Place[] => {
      const trimmed = query.trim();
      if (!trimmed || !places || !index) return [];

      const ids = index.search(trimmed, { limit }) as number[];
      return ids.map((id) => places[id]).filter((p): p is Place => p != null);
    },
    [places, index],
  );

  const value = useMemo<PlaceSearchContextValue>(
    () => ({
      search,
      places,
      ready,
      error,
      retry,
    }),
    [search, places, ready, error, retry],
  );

  return (
    <PlaceSearchContext.Provider value={value}>
      {children}
    </PlaceSearchContext.Provider>
  );
}

export function usePlaceSearch(): PlaceSearchContextValue {
  const ctx = useContext(PlaceSearchContext);
  if (!ctx) {
    throw new Error("usePlaceSearch must be used within PlaceSearchProvider");
  }
  return ctx;
}
